import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AssistantService } from './assistant.service';
import {
  AssistantActionResult,
  AssistantErrorCode,
  ChatActionCard,
  ChatAttachment,
  ChatEntry,
  ReceiptErrorCode,
} from '../models/assistant.model';

const STORAGE_PREFIX = 'ezri.chat.v1.';
const MAX_STORED_ENTRIES = 60;

/** localStorage: conversations already asked for feedback, and when we last asked. */
export const EZRI_FEEDBACK_ASKED_PREFIX = 'ezri.feedback.asked.';
export const EZRI_FEEDBACK_LAST_ASKED_KEY = 'ezri.feedback.lastAsked';
/** A conversation is worth asking about after this many user messages. */
export const FEEDBACK_MIN_MESSAGES = 2;
/** Ask at most this often, so a regular user is not asked after every chat. */
export const FEEDBACK_INTERVAL_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * A receipt that was read but whose message did not go through: the next
 * send carries it, so trying again does not pay for a second scan.
 */
export interface StagedReceipt {
  attachmentId: string;
  attachment: ChatAttachment;
  /** An object URL of the picture, in memory only. */
  thumbnail: string | null;
}

interface StoredChat {
  conversationId: string | null;
  messagesLeft: number | null;
  limitReached: boolean;
  entries: ChatEntry[];
}

/**
 * Ezri's transcript for this tab. It lives in a root service so closing the
 * panel keeps the conversation, and it is mirrored to sessionStorage (per
 * user) so a reload continues the same server conversation with its history
 * on screen. Storage can be unavailable; everything works in memory then.
 */
@Injectable({ providedIn: 'root' })
export class AssistantChatStore {
  private readonly api = inject(AssistantService);

  private storageKey: string | null = null;
  private nextId = 1;

  readonly entries = signal<ChatEntry[]>([]);
  readonly conversationId = signal<string | null>(null);
  readonly messagesLeft = signal<number | null>(null);
  /** Free plan messages left this month, as of the last reply; null until one, or with no monthly limit. */
  readonly monthlyMessagesLeft = signal<number | null>(null);
  readonly sending = signal(false);
  /**
   * What the user has typed and not sent. Here rather than in the chat
   * component, so folding the panel (a press outside it) keeps it.
   */
  readonly draft = signal('');
  /** The conversation hit its message cap; only a new conversation can go on. */
  readonly limitReached = signal(false);

  /** The user message whose receipt is being read right now. */
  readonly scanningEntryId = signal<number | null>(null);
  readonly stagedReceipt = signal<StagedReceipt | null>(null);
  /**
   * Pictures of the receipts on user messages, as object URLs by entry id. In
   * memory only: a reload keeps the file's name, not the picture.
   */
  readonly thumbnails = signal<ReadonlyMap<number, string>>(new Map());

  readonly isEmpty = computed(() => this.entries().length === 0);
  readonly userMessageCount = computed(() => this.entries().filter(e => e.kind === 'user').length);

  /**
   * Whether leaving this conversation should ask what the user thought: it
   * was a real conversation, and we have not asked about it, or about any
   * conversation lately.
   */
  shouldAskFeedback(now = Date.now()): boolean {
    const id = this.conversationId();
    if (!id || this.userMessageCount() < FEEDBACK_MIN_MESSAGES) return false;
    try {
      if (localStorage.getItem(EZRI_FEEDBACK_ASKED_PREFIX + id)) return false;
      const last = Number(localStorage.getItem(EZRI_FEEDBACK_LAST_ASKED_KEY));
      return !(last > 0 && now - last < FEEDBACK_INTERVAL_MS);
    } catch {
      return true;
    }
  }

  /** Remembers that this conversation was asked about, answered or not. */
  markFeedbackAsked(now = Date.now()): void {
    const id = this.conversationId();
    try {
      if (id) localStorage.setItem(EZRI_FEEDBACK_ASKED_PREFIX + id, '1');
      localStorage.setItem(EZRI_FEEDBACK_LAST_ASKED_KEY, String(now));
    } catch { /* at worst we ask again */ }
  }

  /** Sends the feedback; false when it did not go through (the user is not held up by it). */
  async submitFeedback(rating: number, comment: string, uiLanguage?: string): Promise<boolean> {
    try {
      await firstValueFrom(this.api.sendFeedback({
        conversationId: this.conversationId(),
        rating,
        comment: comment.trim() || undefined,
        uiLanguage,
      }));
      return true;
    } catch {
      return false;
    }
  }

  /** Loads the stored transcript of this user (call once the user is known). */
  restore(userId: string | null | undefined): void {
    const key = STORAGE_PREFIX + (userId || 'anon');
    if (key === this.storageKey) return;
    this.storageKey = key;
    this.draft.set('');
    this.dropStagedReceipt();
    this.reset();
    // Another user's count; this one's comes with the config, then with each reply.
    this.monthlyMessagesLeft.set(null);
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return;
      const stored = JSON.parse(raw) as StoredChat;
      const entries = Array.isArray(stored.entries) ? stored.entries : [];
      // A confirm that was in flight when the page went away is unknown; let the user retry.
      for (const e of entries) {
        if (e.kind === 'assistant' && e.card?.state === 'busy') e.card.state = 'pending';
      }
      this.entries.set(entries);
      this.conversationId.set(stored.conversationId ?? null);
      this.messagesLeft.set(typeof stored.messagesLeft === 'number' ? stored.messagesLeft : null);
      this.limitReached.set(!!stored.limitReached);
      this.nextId = entries.reduce((max, e) => Math.max(max, e.id), 0) + 1;
    } catch {
      /* storage blocked or corrupt: start empty */
    }
  }

  /**
   * Sends one user message. On failure the user's bubble is taken back (the
   * caller puts the text back in the input) and an error line is shown.
   * Resolves to the error code, or null on success.
   */
  async send(text: string, uiLanguage: string | undefined): Promise<AssistantErrorCode | null> {
    const message = text.trim();
    if (!message || this.sending()) return null;

    // A receipt already read rides along with the next message.
    const staged = this.stagedReceipt();
    this.stagedReceipt.set(null);

    const userEntry: ChatEntry = staged
      ? { kind: 'user', id: this.nextId++, text: message, attachment: staged.attachment }
      : { kind: 'user', id: this.nextId++, text: message };
    if (staged?.thumbnail) this.setThumbnail(userEntry.id, staged.thumbnail);
    this.entries.update(list => [...list, userEntry]);
    this.sending.set(true);
    this.persist();

    return this.deliver(userEntry, message, uiLanguage, staged);
  }

  /**
   * Reads a receipt (POST /api/receipts/scan), then sends `text` with it. The
   * user's bubble shows the file while it is read. A scan that fails takes the
   * bubble back and says why; a message that fails after the scan keeps the
   * receipt staged for the next send.
   */
  async sendReceipt(
    file: Blob,
    fileName: string,
    text: string,
    uiLanguage: string | undefined,
  ): Promise<ReceiptErrorCode | AssistantErrorCode | null> {
    const message = text.trim();
    if (!message || this.sending()) return null;

    this.dropStagedReceipt();
    const attachment: ChatAttachment = {
      name: fileName,
      kind: file.type === 'application/pdf' || /\.pdf$/i.test(fileName) ? 'pdf' : 'image',
    };
    const userEntry: ChatEntry = { kind: 'user', id: this.nextId++, text: message, attachment };
    const thumbnail = attachment.kind === 'image' ? objectUrl(file) : null;
    if (thumbnail) this.setThumbnail(userEntry.id, thumbnail);
    this.entries.update(list => [...list, userEntry]);
    this.scanningEntryId.set(userEntry.id);
    this.sending.set(true);
    this.persist();

    let attachmentId: string;
    try {
      attachmentId = (await firstValueFrom(this.api.scanReceipt(file, fileName, uiLanguage))).attachmentId;
    } catch (err) {
      const code = AssistantService.receiptErrorCode(err);
      this.removeThumbnail(userEntry.id, true);
      this.entries.update(list => [
        ...list.filter(e => e.id !== userEntry.id),
        { kind: 'receipt_error', id: this.nextId++, code },
      ]);
      this.scanningEntryId.set(null);
      this.sending.set(false);
      this.persist();
      return code;
    }

    this.scanningEntryId.set(null);
    return this.deliver(userEntry, message, uiLanguage, { attachmentId, attachment, thumbnail });
  }

  /** A receipt refused before it was sent (too big, or the sample did not load). */
  showReceiptError(code: ReceiptErrorCode): void {
    this.entries.update(list => [...list, { kind: 'receipt_error', id: this.nextId++, code }]);
    this.persist();
  }

    /** Forgets a receipt that was read but not sent. */
  dropStagedReceipt(): void {
    const staged = this.stagedReceipt();
    if (!staged) return;
    if (staged.thumbnail) revoke(staged.thumbnail);
    this.stagedReceipt.set(null);
  }

  private async deliver(
    userEntry: ChatEntry,
    message: string,
    uiLanguage: string | undefined,
    receipt: StagedReceipt | null,
  ): Promise<AssistantErrorCode | null> {
    try {
      const reply = await firstValueFrom(this.api.sendMessage({
        conversationId: this.conversationId(),
        message,
        uiLanguage,
        ...(receipt ? { attachmentId: receipt.attachmentId } : {}),
      }));
      this.conversationId.set(reply.conversationId ?? this.conversationId());
      this.messagesLeft.set(typeof reply.messagesLeft === 'number' ? reply.messagesLeft : null);
      if (reply.messagesLeft === 0) this.limitReached.set(true);
      this.monthlyMessagesLeft.set(typeof reply.monthlyMessagesLeft === 'number' ? reply.monthlyMessagesLeft : null);

      const card: ChatActionCard | undefined = reply.pendingAction
        ? { action: reply.pendingAction, state: 'pending' }
        : undefined;
      if (reply.reply || card) {
        this.entries.update(list => [...list, { kind: 'assistant', id: this.nextId++, text: reply.reply ?? '', card }]);
      }
      return null;
    } catch (err) {
      const code = AssistantService.errorCode(err);
      // Capped, or the conversation is not this user's any more (not_found):
      // either way only a new conversation can go on.
      if (code === 'limit_conversation' || code === 'not_found') this.limitReached.set(true);
      // The month's, not the conversation's: a new conversation does not help.
      if (code === 'limit_monthly') this.monthlyMessagesLeft.set(0);
      // The receipt was read: keep it for the next try, unless the server no longer has it.
      if (receipt && code !== 'invalid_attachment') {
        this.removeThumbnail(userEntry.id, false);
        this.stagedReceipt.set(receipt);
      } else {
        this.removeThumbnail(userEntry.id, true);
      }
      this.entries.update(list => [
        ...list.filter(e => e.id !== userEntry.id),
        // The footer says it, with the way to upgrade, in place of the input.
        ...(code === 'limit_monthly' ? [] : [{ kind: 'error' as const, id: this.nextId++, code }]),
      ]);
      return code;
    } finally {
      this.sending.set(false);
      this.persist();
    }
  }

  confirm(entryId: number): Promise<void> {
    return this.resolveAction(entryId, id => this.api.confirmAction(id));
  }

  cancel(entryId: number): Promise<void> {
    return this.resolveAction(entryId, id => this.api.cancelAction(id));
  }

  /** Forgets the conversation; the next message starts a new one. */
  newConversation(): void {
    this.reset();
    this.persist();
  }

  private async resolveAction(
    entryId: number,
    call: (actionId: string) => ReturnType<AssistantService['confirmAction']>,
  ): Promise<void> {
    const entry = this.entries().find(e => e.id === entryId);
    if (!entry || entry.kind !== 'assistant' || !entry.card || entry.card.state !== 'pending') return;
    const actionId = entry.card.action.id;

    this.updateCard(entryId, { state: 'busy', errorCode: undefined });
    try {
      const result: AssistantActionResult = await firstValueFrom(call(actionId));
      this.updateCard(entryId, { state: 'resolved', resultCode: result.code || (result.ok ? 'done' : 'failed'), link: result.link ?? null });
    } catch (err) {
      const code = AssistantService.errorCode(err);
      if (code === 'action_not_pending' || code === 'not_found' || (err instanceof HttpErrorResponse && err.status === 409)) {
        this.updateCard(entryId, { state: 'resolved', resultCode: 'not_pending' });
      } else {
        this.updateCard(entryId, { state: 'pending', errorCode: code });
      }
    } finally {
      this.persist();
    }
  }

  private updateCard(entryId: number, patch: Partial<ChatActionCard>): void {
    this.entries.update(list => list.map(e =>
      e.id === entryId && e.kind === 'assistant' && e.card ? { ...e, card: { ...e.card, ...patch } } : e));
  }

  private setThumbnail(entryId: number, url: string): void {
    this.thumbnails.update(map => new Map(map).set(entryId, url));
  }

  private removeThumbnail(entryId: number, revokeUrl: boolean): void {
    const url = this.thumbnails().get(entryId);
    if (!url) return;
    if (revokeUrl) revoke(url);
    this.thumbnails.update(map => {
      const next = new Map(map);
      next.delete(entryId);
      return next;
    });
  }

  private reset(): void {
    for (const url of this.thumbnails().values()) revoke(url);
    this.thumbnails.set(new Map());
    // A staged receipt stays: it was read, and a new conversation may still use it.
    this.scanningEntryId.set(null);
    this.entries.set([]);
    this.conversationId.set(null);
    this.messagesLeft.set(null);
    this.limitReached.set(false);
    this.sending.set(false);
    this.nextId = 1;
  }

  private persist(): void {
    if (!this.storageKey) return;
    try {
      const stored: StoredChat = {
        conversationId: this.conversationId(),
        messagesLeft: this.messagesLeft(),
        limitReached: this.limitReached(),
        entries: this.entries().slice(-MAX_STORED_ENTRIES),
      };
      sessionStorage.setItem(this.storageKey, JSON.stringify(stored));
    } catch {
      /* storage full or blocked: keep it in memory only */
    }
  }
}

function objectUrl(file: Blob): string | null {
  try { return URL.createObjectURL(file); } catch { return null; }
}

function revoke(url: string): void {
  try { URL.revokeObjectURL(url); } catch { /* nothing to free */ }
}
